import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Avatar,
  Grid,
  TextField,
  Tabs,
  Tab,
  IconButton,
  Alert
} from '@mui/material';
import { PhotoCamera, Close as CloseIcon } from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../config';

interface AvatarCustomizationProps {
  open: boolean;
  onClose: () => void;
  currentInitial: string;
  currentAvatarType?: 'initial' | 'image';
  currentAvatarUrl?: string;
  currentAvatarColor?: string;
  onSave: () => void;
}

const AVATAR_COLORS = [
  '#1976d2', // blue
  '#d32f2f', // red
  '#388e3c', // green
  '#f57c00', // orange
  '#7b1fa2', // purple
  '#0097a7', // cyan
  '#c2185b', // pink
  '#5d4037', // brown
  '#455a64', // blue grey
  '#00796b', // teal
];

const AvatarCustomization: React.FC<AvatarCustomizationProps> = ({
  open,
  onClose,
  currentInitial,
  currentAvatarType,
  currentAvatarUrl,
  currentAvatarColor,
  onSave
}) => {
  const [tabValue, setTabValue] = useState(currentAvatarType === 'image' ? 1 : 0);
  const [selectedColor, setSelectedColor] = useState(currentAvatarColor || '#1976d2');
  const [customInitial, setCustomInitial] = useState(currentInitial);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [currentImage, setCurrentImage] = useState<string | null>(currentAvatarUrl || null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');

  // Reset state when modal opens with new data
  useEffect(() => {
    if (open) {
      setTabValue(currentAvatarType === 'image' ? 1 : 0);
      setSelectedColor(currentAvatarColor || '#1976d2');
      setCustomInitial(currentInitial);
      setCurrentImage(currentAvatarUrl || null);
      setUploadedImage(null);
      setError('');
    }
  }, [open, currentAvatarType, currentAvatarColor, currentInitial, currentAvatarUrl]);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('圖片大小不能超過 5MB');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('請上傳圖片檔案');
      return;
    }

    setError('');
    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('avatar', file);

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/auth/upload-avatar`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setUploadedImage(response.data.avatarUrl);
        // Don't call onSave() here - let user see the preview and close manually
      }
    } catch (err: any) {
      if (axios.isAxiosError(err) && err.response) {
        setError(err.response.data.message || '上傳失敗');
      } else {
        setError('上傳時發生錯誤');
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleSaveInitial = async () => {
    if (!customInitial.trim()) {
      setError('請輸入文字');
      return;
    }

    setError('');
    setIsUploading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/auth/avatar-settings`,
        {
          avatarType: 'initial',
          avatarInitial: customInitial.trim().substring(0, 2), // Preserve emojis (up to 2 chars)
          avatarColor: selectedColor
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      onSave();
      onClose();
    } catch (err: any) {
      if (axios.isAxiosError(err) && err.response) {
        setError(err.response.data.message || '儲存失敗');
      } else {
        setError('儲存時發生錯誤');
      }
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        自訂頭像
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 3 }}>
          <Tab label="文字頭像" />
          <Tab label="上傳圖片" />
        </Tabs>

        {/* Initial Avatar Tab */}
        {tabValue === 0 && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              選擇顏色和文字來建立您的頭像
            </Typography>

            {/* Preview */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <Avatar
                sx={{
                  width: 120,
                  height: 120,
                  bgcolor: selectedColor,
                  fontSize: '3rem',
                  fontWeight: 'bold'
                }}
              >
                {customInitial.substring(0, 2) || '?'}
              </Avatar>
            </Box>

            {/* Initial Input */}
            <TextField
              fullWidth
              label="顯示文字"
              value={customInitial}
              onChange={(e) => setCustomInitial(e.target.value.slice(0, 2))}
              placeholder="輸入 1-2 個字元"
              inputProps={{ maxLength: 2 }}
              sx={{ mb: 3 }}
            />

            {/* Color Selection */}
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              選擇顏色
            </Typography>
            <Grid container spacing={1}>
              {AVATAR_COLORS.map((color) => (
                <Grid item key={color}>
                  <Box
                    onClick={() => setSelectedColor(color)}
                    sx={{
                      width: 48,
                      height: 48,
                      bgcolor: color,
                      borderRadius: '50%',
                      cursor: 'pointer',
                      border: selectedColor === color ? '3px solid #000' : '3px solid transparent',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'scale(1.1)'
                      }
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Upload Image Tab */}
        {tabValue === 1 && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              上傳您的個人照片作為頭像（最大 5MB）
            </Typography>

            {/* Show current profile picture if exists */}
            {currentImage && !uploadedImage && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  目前的頭像
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <Avatar
                    src={currentImage}
                    sx={{ width: 120, height: 120 }}
                  />
                </Box>
              </Box>
            )}

            {uploadedImage ? (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  新的頭像
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <Avatar
                    src={uploadedImage}
                    sx={{ width: 120, height: 120 }}
                  />
                </Box>
                <Alert severity="success" sx={{ mb: 2 }}>
                  頭像上傳成功！點擊「完成」以套用變更。
                </Alert>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  startIcon={<PhotoCamera />}
                  disabled={isUploading}
                  sx={{ py: 2 }}
                >
                  更換圖片
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageUpload}
                  />
                </Button>
              </Box>
            ) : (
              <Box>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  startIcon={<PhotoCamera />}
                  disabled={isUploading}
                  sx={{ py: 2 }}
                >
                  {isUploading ? '上傳中...' : currentImage ? '更換圖片' : '選擇圖片'}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageUpload}
                  />
                </Button>

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                  支援 JPG、PNG、GIF 格式
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isUploading}>
          {uploadedImage && tabValue === 1 ? '取消' : '取消'}
        </Button>
        {tabValue === 0 && (
          <Button
            variant="contained"
            onClick={handleSaveInitial}
            disabled={isUploading || !customInitial.trim()}
          >
            儲存
          </Button>
        )}
        {tabValue === 1 && uploadedImage && (
          <Button
            variant="contained"
            onClick={() => {
              onSave();
              onClose();
            }}
            disabled={isUploading}
          >
            完成
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AvatarCustomization;
